import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { trpc } from '~/api/trpc';
import { cancellationFeeTypeLabels, type CancellationFeeType } from '~/lib/cancellation-fee-config';

const ALL_STORES = 'all';
const CANCELLATION_FEE_VAT_RATE = 0;

const tierFormSchema = z.object({
    feeType: z.enum(['NO_SHOW', 'LATE_CANCELLATION']),
    storeId: z.string(),
    name: z.string().trim().min(1, 'Name erforderlich'),
    minDurationMinutes: z.string().trim().regex(/^\d+$/, 'Ganze Minuten, mindestens 0'),
    maxDurationMinutes: z.string(),
    unitPriceGross: z.string().trim().refine((value) => Number(value) > 0, 'Betrag muss größer als 0 sein'),
    isActive: z.boolean(),
}).superRefine((data, ctx) => {
    if (data.maxDurationMinutes.trim() === '') {
        return;
    }
    const max = Number(data.maxDurationMinutes);
    if (!Number.isInteger(max) || max <= Number(data.minDurationMinutes)) {
        ctx.addIssue({
            code: 'custom',
            path: ['maxDurationMinutes'],
            message: 'Muss eine ganze Zahl größer als die minimale Dauer sein',
        });
    }
});

type TierFormValues = z.infer<typeof tierFormSchema>;

export type CancellationFeeTierRow = {
    id: string;
    storeId: number | null;
    feeType: CancellationFeeType;
    name: string;
    minDurationMinutes: number;
    maxDurationMinutes: number | null;
    unitPriceGross: number;
    vatRate: number;
    isActive: boolean;
};

type CancellationFeeTierDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tier: CancellationFeeTierRow | null;
};

const toFormValues = (tier: CancellationFeeTierRow | null): TierFormValues => ({
    feeType: tier?.feeType ?? 'NO_SHOW',
    storeId: tier?.storeId ? String(tier.storeId) : ALL_STORES,
    name: tier?.name ?? '',
    minDurationMinutes: tier ? String(tier.minDurationMinutes) : '0',
    maxDurationMinutes: tier?.maxDurationMinutes != null ? String(tier.maxDurationMinutes) : '',
    unitPriceGross: tier ? String(tier.unitPriceGross) : '',
    isActive: tier?.isActive ?? true,
});

export const CancellationFeeTierDialog = ({ open, onOpenChange, tier }: CancellationFeeTierDialogProps) => {
    const trpcUtils = trpc.useUtils();
    const { data: stores } = trpc.stores.getAllSimple.useQuery(undefined, { enabled: open });

    const form = useForm<TierFormValues>({
        resolver: zodResolver(tierFormSchema),
        defaultValues: toFormValues(tier),
    });

    useEffect(() => {
        if (open) {
            form.reset(toFormValues(tier));
        }
    }, [open, tier, form]);

    const onSaved = (message: string) => {
        toast.success(message);
        trpcUtils.cancellationFees.getTiers.invalidate();
        trpcUtils.cancellationFees.getTiersForAppointment.invalidate();
        onOpenChange(false);
    };

    const createMutation = trpc.cancellationFees.createTier.useMutation({
        onSuccess: () => onSaved('Gebührenstufe erstellt'),
        onError: (error) => toast.error(error.message),
    });
    const updateMutation = trpc.cancellationFees.updateTier.useMutation({
        onSuccess: () => onSaved('Gebührenstufe gespeichert'),
        onError: (error) => toast.error(error.message),
    });
    const isSaving = createMutation.isPending || updateMutation.isPending;

    const onSubmit = (values: TierFormValues) => {
        const data = {
            feeType: values.feeType,
            storeId: values.storeId === ALL_STORES ? undefined : Number(values.storeId),
            name: values.name,
            minDurationMinutes: Number(values.minDurationMinutes),
            maxDurationMinutes: values.maxDurationMinutes.trim() === '' ? undefined : Number(values.maxDurationMinutes),
            unitPriceGross: Number(values.unitPriceGross),
            vatRate: CANCELLATION_FEE_VAT_RATE,
            isActive: values.isActive,
        };
        if (tier) {
            updateMutation.mutate({ id: tier.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-lg'>
                <DialogHeader>
                    <DialogTitle>{tier ? 'Gebührenstufe bearbeiten' : 'Neue Gebührenstufe'}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='feeType'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Typ *</FormLabel>
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {(Object.keys(cancellationFeeTypeLabels) as CancellationFeeType[]).map((type) => (
                                                    <SelectItem key={type} value={type}>
                                                        {cancellationFeeTypeLabels[type]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='storeId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Filiale *</FormLabel>
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value={ALL_STORES}>Alle Filialen</SelectItem>
                                                {stores?.map((store) => (
                                                    <SelectItem key={store.id} value={String(store.id)}>
                                                        {store.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name *</FormLabel>
                                    <FormControl>
                                        <Input placeholder='z. B. Ausfallentschädigung Termin 30–60 Min.' {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='minDurationMinutes'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Dauer ab (Min.) *</FormLabel>
                                        <FormControl>
                                            <Input type='number' min={0} step={1} {...field} />
                                        </FormControl>
                                        <FormDescription>inklusive</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='maxDurationMinutes'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Dauer bis (Min.)</FormLabel>
                                        <FormControl>
                                            <Input type='number' min={1} step={1} placeholder='unbegrenzt' {...field} />
                                        </FormControl>
                                        <FormDescription>exklusive, leer = unbegrenzt</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='unitPriceGross'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Betrag brutto (€) *</FormLabel>
                                        <FormControl>
                                            <Input type='number' min={0} step={0.01} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className='space-y-2'>
                                <Label htmlFor='cancellation-fee-vat-rate'>MwSt. (%)</Label>
                                <Input id='cancellation-fee-vat-rate' type='number' value={CANCELLATION_FEE_VAT_RATE} disabled readOnly />
                                <p className='text-sm text-muted-foreground'>Ausfallentschädigungen sind umsatzsteuerfrei</p>
                            </div>
                        </div>

                        <FormField
                            control={form.control}
                            name='isActive'
                            render={({ field }) => (
                                <FormItem className='flex items-center justify-between rounded-md border p-3'>
                                    <FormLabel className='m-0'>Aktiv</FormLabel>
                                    <FormControl>
                                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button type='submit' disabled={isSaving}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
